import React, { useEffect, useId, useState } from 'react';
import {
  MenuItem,
  MenuItemDetail,
  MenuModifierGroup,
  MenuModifierOption,
} from '../../../services/restaurantService';
import {
  getAvailableSizes,
  getDefaultSizeTypeId,
  getVariantUnitPrice,
} from '../../../utils/menuPricing';
import {
  appendNotePreset,
  getVariantLabel,
  getVariantName,
  groupMax,
  groupMin,
  groupsForVariant,
  isOptionAvailable,
  isSingleSelectGroup,
  MenuOrderSelections,
  noteLength,
  optionPrice,
  pruneSelections,
  toggleOption,
  validateSelections,
} from '../../../utils/menuOptions';
import type { GuestNoteSettings } from '../../../hooks/useGuestNoteSettings';
import './MenuOrderOptions.css';

export type { MenuOrderSelections } from '../../../utils/menuOptions';
export type ModifierGroup = MenuModifierGroup;
export type ModifierGroupOption = MenuModifierOption;

export const NOTE_HELPER_TEXT =
  "Requests are passed to the kitchen and may not always be possible. Paid extras can't be added here.";

interface MenuOrderOptionsProps {
  item: MenuItem | MenuItemDetail;
  selections: MenuOrderSelections;
  onChange: (selections: MenuOrderSelections) => void;
  compact?: boolean;
  formatPrice?: (price: number) => string;
  /** When item notes are enabled for the outlet, shows "Add cooking request". */
  noteSettings?: GuestNoteSettings;
}

const defaultFormatPrice = (price: number) => `₹${Math.round(price)}`;

function groupBadge(group: MenuModifierGroup): string {
  const min = groupMin(group);
  const max = groupMax(group);
  if (min > 0) {
    if (min > 1 && max === min) return `Required · Choose ${min}`;
    if (max !== Infinity && max > min) return `Required · Choose up to ${max}`;
    return 'Required';
  }
  if (max !== Infinity && max > 1) return `Choose up to ${max}`;
  return 'Optional';
}

const DietDot: React.FC<{ tag?: string | null }> = ({ tag }) => {
  if (!tag) return null;
  const t = tag.toUpperCase();
  const cls = t === 'NON_VEG' ? 'non-veg' : t === 'EGG' ? 'egg' : 'veg';
  const label = t === 'NON_VEG' ? 'Non-vegetarian' : t === 'EGG' ? 'Contains egg' : 'Vegetarian';
  return (
    <span
      className={`menu-order-options__diet menu-order-options__diet--${cls}`}
      role="img"
      aria-label={label}
    />
  );
};

const MenuOrderOptions: React.FC<MenuOrderOptionsProps> = ({
  item,
  selections,
  onChange,
  compact = false,
  formatPrice = defaultFormatPrice,
  noteSettings,
}) => {
  const uid = useId();
  const sizes = getAvailableSizes(item);
  const showSizes = sizes.length > 1;
  const variantLabel = getVariantLabel(item);
  const groups = groupsForVariant(item, selections.sizeTypeId);
  const picked = selections.modifierOptionIds || [];
  const notesEnabled = noteSettings?.itemNotesEnabled === true;
  const noteMax = noteSettings?.itemNoteMaxLength ?? 200;
  const [noteOpen, setNoteOpen] = useState<boolean>(() => !!selections.itemNote);

  useEffect(() => {
    if (selections.itemNote) {
      setNoteOpen(true);
    }
  }, [selections.itemNote]);

  if (!showSizes && groups.length === 0 && !notesEnabled) {
    return null;
  }

  const selectVariant = (sizeTypeId: number) => {
    onChange(pruneSelections(item, { ...selections, sizeTypeId }));
  };

  const noteLen = noteLength(selections.itemNote);
  const presets = noteSettings?.presets || [];

  return (
    <div className={`menu-order-options ${compact ? 'menu-order-options--compact' : ''}`}>
      {showSizes && (
        <section className="menu-order-options__group">
          <div className="menu-order-options__group-head">
            <h4 className="menu-order-options__label" id={`${uid}-variant`}>
              {variantLabel}
            </h4>
            <span className="menu-order-options__badge menu-order-options__badge--required">Required</span>
          </div>
          <div className="menu-order-options__chips" role="radiogroup" aria-labelledby={`${uid}-variant`}>
            {sizes.map((size) => {
              const selected = selections.sizeTypeId === size.sizeTypeId;
              const name = getVariantName(size);
              // Flat promotions are once per bill, so the chip shows the variant's own price.
              const price = getVariantUnitPrice(item, { sizeTypeId: size.sizeTypeId });
              return (
                <button
                  key={size.sizeTypeId}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={`${name}, ${formatPrice(price)}`}
                  className={`menu-order-options__chip menu-order-options__chip--variant ${
                    selected ? 'is-selected' : ''
                  }`}
                  onClick={() => selectVariant(size.sizeTypeId)}
                >
                  <span className="menu-order-options__chip-name">{name}</span>
                  <span className="menu-order-options__chip-price">{formatPrice(price)}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {groups.map((group) => {
        const single = isSingleSelectGroup(group);
        const options = group.options || [];
        const groupIds = options.map((o) => o.id);
        const pickedInGroup = picked.filter((id) => groupIds.indexOf(id) >= 0).length;
        const atMax = !single && pickedInGroup >= groupMax(group);
        const headingId = `${uid}-group-${group.id}`;
        const required = groupMin(group) > 0;
        return (
          <section key={group.id} className="menu-order-options__group">
            <div className="menu-order-options__group-head">
              <h4 className="menu-order-options__label" id={headingId}>
                {group.groupName || group.groupCode}
              </h4>
              <span
                className={`menu-order-options__badge ${
                  required ? 'menu-order-options__badge--required' : ''
                }`}
              >
                {groupBadge(group)}
              </span>
            </div>
            <div
              className="menu-order-options__list"
              role={single ? 'radiogroup' : 'group'}
              aria-labelledby={headingId}
            >
              {options.map((opt) => {
                const isPicked = picked.indexOf(opt.id) >= 0;
                const available = isOptionAvailable(opt, selections.sizeTypeId);
                const capped = atMax && !isPicked;
                const price = optionPrice(opt, selections.sizeTypeId);
                const name = opt.optionName || opt.optionCode || '';
                const priceLabel = price > 0 ? `, plus ${formatPrice(price)}` : '';
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role={single ? 'radio' : 'checkbox'}
                    aria-checked={isPicked}
                    disabled={!available}
                    // At the group maximum the option stays focusable; the tap is ignored.
                    aria-disabled={capped || undefined}
                    aria-label={`${name}${priceLabel}${available ? '' : ', out of stock'}${
                      capped ? `, limit of ${groupMax(group)} reached` : ''
                    }`}
                    className={`menu-order-options__option ${isPicked ? 'is-selected' : ''} ${
                      !available ? 'is-unavailable' : ''
                    } ${capped ? 'is-capped' : ''}`}
                    onClick={() => {
                      if (!available || capped) return;
                      onChange(toggleOption(selections, group, opt.id));
                    }}
                  >
                    <span
                      className={`menu-order-options__indicator menu-order-options__indicator--${
                        single ? 'radio' : 'checkbox'
                      }`}
                      aria-hidden="true"
                    />
                    <DietDot tag={opt.dietTag} />
                    <span className="menu-order-options__option-name">{name}</span>
                    <span className="menu-order-options__option-price">
                      {!available ? 'Out of stock' : price > 0 ? `+${formatPrice(price)}` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {notesEnabled && (
        <section className="menu-order-options__note">
          <button
            type="button"
            className="menu-order-options__note-link"
            onClick={() => setNoteOpen((open) => !open)}
            aria-expanded={noteOpen}
            aria-controls={`${uid}-note-panel`}
          >
            <i className={`fa ${noteOpen ? 'fa-chevron-up' : 'fa-pencil'}`} aria-hidden="true" />{' '}
            {noteOpen
              ? 'Hide cooking request'
              : selections.itemNote?.trim()
                ? 'Edit cooking request'
                : 'Add cooking request'}
          </button>
          {noteOpen && (
            <div id={`${uid}-note-panel`} className="menu-order-options__note-panel">
              <label className="menu-order-options__label" htmlFor={`${uid}-note`}>
                Cooking request
              </label>
              <textarea
                id={`${uid}-note`}
                className="menu-order-options__note-input"
                placeholder="e.g. less spicy, no onion"
                value={selections.itemNote || ''}
                maxLength={noteMax}
                rows={2}
                onChange={(e) => onChange({ ...selections, itemNote: e.target.value })}
                aria-describedby={`${uid}-note-help`}
              />
              <div className="menu-order-options__note-meta">
                <span id={`${uid}-note-help`} className="menu-order-options__note-help">
                  {NOTE_HELPER_TEXT}
                </span>
                <span
                  className={`menu-order-options__note-count ${noteLen > noteMax ? 'is-over' : ''}`}
                  aria-live="polite"
                >
                  {noteLen}/{noteMax}
                </span>
              </div>
              {presets.length > 0 && (
                <div className="menu-order-options__chips" role="group" aria-label="Quick requests">
                  {presets.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className="menu-order-options__chip menu-order-options__chip--preset"
                      onClick={() =>
                        onChange({
                          ...selections,
                          itemNote: appendNotePreset(selections.itemNote, preset, noteMax),
                        })
                      }
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export function validateOrderSelections(
  item: MenuItem | MenuItemDetail,
  selections: MenuOrderSelections,
  itemNoteMax?: number
): string | null {
  return validateSelections(item, selections, itemNoteMax);
}

/** Default variant, no add-ons, no note. */
export function getInitialSelections(item: MenuItem | MenuItemDetail): MenuOrderSelections {
  const sizes = getAvailableSizes(item);
  const sizeTypeId = sizes.length === 1 ? sizes[0].sizeTypeId : getDefaultSizeTypeId(item);
  return { sizeTypeId, modifierOptionIds: [], itemNote: '' };
}

export default MenuOrderOptions;
